import React, { useState, useRef } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { PriceBreakdownCard } from './PriceBreakdownCard';
import { formatCurrency } from '../utils/pricingCalculations';

/**
 * Clickable price cell that opens a popover with price breakdown details.
 *
 * Per CONTEXT.md decisions:
 * - "Popover on click - Click price to open popover with breakdown"
 * - "Show template name - 'Calculated using: Standard 30%' at top of popover"
 * - "Show both margin and markup - Display equivalent: 'Margin: 30% (= 43% markup)'"
 *
 * @param {object} props
 * @param {number} props.price - The calculated price to display
 * @param {object} props.breakdown - Full breakdown object from priceBreakdown field
 * @param {string} props.templateName - Name of the pricing template used
 * @param {boolean} props.disabled - If true, don't show popover (for products without template)
 */
function PriceColumnPopover({ price, breakdown, templateName, disabled = false }) {
  const [show, setShow] = useState(false);
  const target = useRef(null);

  // If disabled or no breakdown, show simple dash or price
  if (disabled || !breakdown) {
    if (price != null) {
      return <span>{formatCurrency(price)}</span>;
    }
    return <span className="text-muted">-</span>;
  }

  // Calculate markup equivalent for display
  const marginPercent = breakdown.marginPercent || 0;
  const markupEquivalent = marginPercent < 100
    ? Math.round((marginPercent / (100 - marginPercent)) * 100)
    : 0;

  return (
    <>
      <span
        ref={target}
        onClick={(e) => {
          e.stopPropagation();
          setShow(!show);
        }}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline dotted',
          textDecorationColor: '#6c757d'
        }}
        className="text-primary"
        title="Click for price breakdown"
      >
        {formatCurrency(price)}
      </span>
      <Overlay
        target={target.current}
        show={show}
        placement="left"
        rootClose
        onHide={() => setShow(false)}
      >
        {(props) => (
          <Popover {...props} style={{ ...props.style, maxWidth: '400px' }}>
            <Popover.Header className="d-flex justify-content-between align-items-center">
              <strong>Calculated using:</strong>
              <span className="ms-2 text-primary">{templateName || 'Unknown Template'}</span>
            </Popover.Header>
            <Popover.Body className="p-2">
              <PriceBreakdownCard breakdown={breakdown} compact={false} />
              {/* Show margin/markup equivalents per CONTEXT.md decision */}
              <div className="mt-2 small text-muted border-top pt-2">
                <strong>Margin:</strong> {marginPercent}%
                <span className="ms-1">
                  (= {markupEquivalent}% markup)
                </span>
              </div>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </>
  );
}

export default PriceColumnPopover;
